"use client";
import ScreenLayout from "@/components/layout/screen-layout";
import { Button } from "@/components/ui/button";
import { system } from "@/lib/dto/system/system";
import { ImgLogo } from "@/lib/images";
import { IMenuType, ISublink } from "@/lib/types/type-menu";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronRight, ChevronUp } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

export default function DesktopHeaderView({ menu }: { menu: IMenuType[] }) {
  return (
    <header className="sticky top-0  left-0 right-0 z-[100]">
      <ScreenLayout
        outerClassName="z-[100] bg-background relative lg:border-b lg:border-gray-200"
        className=" h-[5rem]  w-full hidden lg:flex"
      >
        {/* Mian Header */}
        <LogoSection />

        {/* Navbar Menu */}
        <NavMenuSection menu={menu} />

        {/* Right Buttons */}
        <RightButtonSection />
      </ScreenLayout>
    </header>
  );
}

function LogoSection() {
  return (
    <div className="flex items-center justify-center">
      <Link href="/">
        <Image
          src={ImgLogo}
          alt={system.company_name}
          width={612}
          height={165}
          className="w-60 md:w-35 h-auto object-contain"
        />
      </Link>
    </div>
  );
}

function NavMenuSection({ menu }: { menu: IMenuType[] }) {
  return (
    <div className="flex justify-end item-center w-full flex-1">
      <nav className="flex items-center w-full justify-center gap-x-8 lg:gap-x-8 xl:gap-x-8 2xl:gap-x-10">
        {menu.map((e, i) => {
          return <NavMenuItem key={`nav-link-${e.title}-${i}`} item={e} />;
        })}
      </nav>
    </div>
  );
}

function NavMenuItem({ item }: { item: IMenuType | ISublink }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div className="relative group">
      {!item.sublinks ? (
        <Link href={item.path}>
          <p className="flex items-center text-[0.7rem] lg:text-[1rem] xl:text-[1.1rem] underline-effect gap-1">
            {item.icon && <item.icon className="w-4 h-4 mr-1" />}
            {item.title}
          </p>
        </Link>
      ) : (
        <span
          className="cursor-default"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <p className="flex items-center text-[0.7rem] lg:text-[1rem] xl:text-[1.1rem] underline-effect gap-3">
            {item.icon && <item.icon className="w-4 h-4 mr-1" />}
            {item.title}
            {item.sublinks ? (
              !hovered ? (
                <ChevronDown
                  size={18}
                  className="mt-[2px] text-[#80939A]"
                />
              ) : (
                <ChevronUp
                  size={18}
                  className="mt-[2px] text-[#80939A]"
                />
              )
            ) : null}
          </p>
        </span>
      )}

      {/*Sublinks*/}
      {item.sublinks && (
        <div
          className={cn(
            "absolute left-0 mt-2 w-64 bg-white border border-gray-200 shadow-lg rounded-sm z-50 transition-all duration-500 ease-in-out opacity-0 group-hover:opacity-100 group-hover:visible invisible",
            hovered
              ? "opacity-100 translate-y-0"
              : "opacity-0 -translate-y-1"
          )}
        >
          <ul className="flex flex-col py-2">
            {item.sublinks.map((sublink, idx) => (
              <li key={`submenu-${sublink.title}-${idx}`} className="relative group/submenu">
                {!sublink.sublinks ? (
                  <Link
                    href={sublink.path}
                    className="inline-flex gap-x-2 w-full items-center px-4 py-2 text-sm hover:bg-zinc-100 text-navy-blue font-semibold"
                  >
                    {sublink.icon && (
                      <sublink.icon className="w-4 h-4 text-navy-blue" />
                    )}
                    {sublink.title}
                  </Link>
                ) : (
                  <div className="inline-flex justify-between w-full items-center px-4 py-2 text-sm hover:bg-zinc-100 text-navy-blue font-semibold cursor-default">
                    <span className="flex gap-x-2 items-center">
                      {sublink.icon && (
                        <sublink.icon className="w-4 h-4 text-navy-blue" />
                      )}
                      {sublink.title}
                    </span>
                    <ChevronRight size={16} className="text-[#80939A]" />

                    {/* Nested submenu */}
                    <div className="absolute left-full top-0 w-64 bg-white border border-gray-200 shadow-lg rounded-sm invisible group-hover/submenu:visible transition-all duration-300">
                      <ul className="flex flex-col py-2">
                        {sublink.sublinks.map((nestedLink, nestedIdx) => (
                          <li key={`nested-submenu-${nestedLink.title}-${nestedIdx}`}>
                            <Link
                              href={nestedLink.path}
                              className="inline-flex gap-x-2 w-full items-center px-4 py-2 text-sm hover:bg-zinc-100 text-navy-blue font-semibold"
                            >
                              {nestedLink.icon && (
                                <nestedLink.icon className="w-4 h-4 text-navy-blue" />
                              )}
                              {nestedLink.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RightButtonSection() {
  return (
    <div className="flex gap-4 items-center">
      <Link href="/case-studies">
        <Button
          variant="outline"
          className="shadow-none rounded-full cursor-pointer px-4 py-2 font-medium text-base"
        >
          <p>Our Work</p>
        </Button>
      </Link>
      <Link href="/contact-us">
        <Button className="shadow-none rounded-full cursor-pointer px-4 py-2 font-medium text-base">
          <p> Build Digital Solutions</p>
        </Button>
      </Link>
    </div>
  );
}
